import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../lib/api';
import { useAnalyticsAccess } from '../../hooks/useAnalyticsAccess';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';

interface AnalyticsData {
  // User metrics
  totalUsers: number;
  newUsers: number;
  activeUsers: number;

  // Content metrics
  totalPosts: number;
  totalComments: number;
  postsThisMonth: number;
  commentsThisMonth: number;

  // Engagement metrics
  engagementRate: number;
  averageReplyRate: number;

  // Business units (countries)
  businessUnits: Array<{
    name: string;
    posts: number;
    active: number;
  }>;

  // Posts by joint
  postsByJoint: Array<{
    name: string;
    value: number;
    color: string;
  }>;

  // Meta data
  month: number;
  year: number;
}

interface RecentViewer {
  name: string;
  email: string;
  lastActive: string;
  postsViewed: number;
  country: string;
}

interface RecentViewersResponse {
  success: boolean;
  count: number;
  viewers: RecentViewer[];
}

export default function Analytics() {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const { hasAnalyticsAccess, accessReason } = useAnalyticsAccess();

  // Check analytics access
  if (!hasAnalyticsAccess) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
            Analytics Access Restricted
          </h3>
          <p className="text-yellow-700 dark:text-yellow-300">
            {accessReason}
          </p>
        </div>
      </div>
    );
  }

  const { data: analytics, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics', selectedMonth, selectedYear],
    queryFn: async () => {
      try {
        return await apiService.request<AnalyticsData>(
          `/api/v1/admin/analytics?month=${selectedMonth}&year=${selectedYear}`
        );
      } catch (err: any) {
        if (err.message?.includes('404') || err.message?.includes('Cannot GET')) {
          throw new Error('Analytics endpoint not implemented yet');
        }
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch Recently Active Users independently (not tied to month selection)
  const { data: recentViewersData, isLoading: isLoadingViewers } = useQuery({
    queryKey: ['recentViewers'],
    queryFn: async () => {
      return await apiService.request<RecentViewersResponse>('/api/v1/admin/recent-viewers');
    },
    staleTime: 2 * 60 * 1000, // Refresh every 2 minutes
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const isEndpointMissing = (error as Error).message?.includes('endpoint not implemented');

    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="theme-card shadow-sm p-8 text-center">
          <div className={`mb-4 ${isEndpointMissing ? 'text-amber-500' : 'text-red-500'}`}>
            {isEndpointMissing ? (
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            ) : (
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>

          {isEndpointMissing ? (
            <>
              <h3 className="text-lg font-semibold theme-text mb-2">Analytics API Not Available</h3>
              <p className="theme-text-muted mb-4">
                The analytics endpoint hasn't been implemented on the backend yet.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold theme-text mb-2">Failed to Load Analytics</h3>
              <p className="theme-text-muted mb-4">Error: {(error as Error).message}</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header with Month/Year Selector */}
      <div className="theme-card shadow-sm p-6 rounded-lg border">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
              <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold theme-text">Analytics Dashboard</h1>
              <p className="text-sm theme-text-muted">
                {monthNames[selectedMonth - 1]} {selectedYear} Report
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="theme-input px-4 py-2 rounded-lg text-sm font-medium border-2 focus:ring-2 focus:ring-blue-500 transition-all"
            >
              {monthNames.map((month, index) => (
                <option key={index} value={index + 1}>
                  {month}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="theme-input px-4 py-2 rounded-lg text-sm font-medium border-2 focus:ring-2 focus:ring-blue-500 transition-all"
            >
              {Array.from({ length: new Date().getFullYear() - 2021 }, (_, i) => 2022 + i).reverse().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards - with better visual hierarchy */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={analytics.totalUsers}
          change={`+${analytics.newUsers} this month`}
          icon="👥"
          color="blue"
        />
        <MetricCard
          title="Active Users"
          subtitle="(Any app activity this month)"
          value={analytics.activeUsers}
          percentage={(analytics.activeUsers / analytics.totalUsers * 100).toFixed(1)}
          icon="🔥"
          color="green"
        />
        <MetricCard
          title="Total Posts"
          value={analytics.totalPosts}
          change={`+${analytics.postsThisMonth} this month`}
          icon="📝"
          color="purple"
        />
        <MetricCard
          title="Engagement Rate"
          value={analytics.engagementRate}
          suffix="%"
          icon="📊"
          color="orange"
        />
      </div>

      {/* Charts Section - Posts by Country & Content Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Posts by Country */}
        <div className="theme-card shadow-sm p-6 rounded-lg border hover:shadow-lg transition-shadow">
          <h3 className="text-lg font-semibold theme-text mb-6 flex items-center">
            <span className="mr-2">🌍</span>
            Posts by Country
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.businessUnits}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                axisLine={{ stroke: 'var(--border-primary)' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                axisLine={{ stroke: 'var(--border-primary)' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar
                dataKey="posts"
                fill="hsl(217, 91%, 60%)"
                radius={[8, 8, 0, 0]}
                className="opacity-80 hover:opacity-100 transition-opacity"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Content Distribution */}
        <div className="theme-card shadow-sm p-6 rounded-lg border hover:shadow-lg transition-shadow">
          <h3 className="text-lg font-semibold theme-text mb-6 flex items-center">
            <span className="mr-2">📊</span>
            Content Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Posts', value: analytics.totalPosts, color: 'hsl(217, 91%, 60%)' },
                  { name: 'Comments', value: analytics.totalComments, color: 'hsl(142, 76%, 36%)' }
                ]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {[
                  { name: 'Posts', value: analytics.totalPosts, color: 'hsl(217, 91%, 60%)' },
                  { name: 'Comments', value: analytics.totalComments, color: 'hsl(142, 76%, 36%)' }
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} className="opacity-80 hover:opacity-100" />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'hsl(217, 91%, 60%)' }}></div>
              <span className="text-sm theme-text-muted font-medium">Posts</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'hsl(142, 76%, 36%)' }}></div>
              <span className="text-sm theme-text-muted font-medium">Comments</span>
            </div>
          </div>
        </div>
      </div>

      {/* Posts by Joint and Monthly Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Posts by Joint Pie Chart */}
        {analytics.postsByJoint && analytics.postsByJoint.length > 0 && (
          <div className="lg:col-span-2 theme-card shadow-sm p-6 rounded-lg border hover:shadow-lg transition-shadow">
            <h3 className="text-lg font-semibold theme-text mb-6 flex items-center">
              <span className="mr-2">🦴</span>
              Posts by Joint
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.postsByJoint}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {analytics.postsByJoint.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} className="opacity-80 hover:opacity-100" />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-4 flex-wrap">
              {analytics.postsByJoint.map((joint, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: joint.color }}></div>
                  <span className="text-sm theme-text-muted font-medium">{joint.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Monthly Activity */}
        <div className="theme-card shadow-sm p-6 rounded-lg border hover:shadow-lg transition-shadow">
          <h3 className="text-lg font-semibold theme-text mb-6 flex items-center">
            <span className="mr-2">📅</span>
            Monthly Activity
          </h3>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium theme-text">Posts</span>
                <span className="text-sm font-bold theme-text">{analytics.postsThisMonth}</span>
              </div>
              <div className="w-full theme-bg-tertiary rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 rounded-full transition-all duration-500 bg-gradient-to-r from-blue-400 to-blue-600"
                  style={{
                    width: `${Math.min((analytics.postsThisMonth / Math.max(analytics.totalPosts * 0.1, 1)) * 100, 100)}%`
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium theme-text">Comments</span>
                <span className="text-sm font-bold theme-text">{analytics.commentsThisMonth}</span>
              </div>
              <div className="w-full theme-bg-tertiary rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 rounded-full transition-all duration-500 bg-gradient-to-r from-green-400 to-green-600"
                  style={{
                    width: `${Math.min((analytics.commentsThisMonth / Math.max(analytics.totalComments * 0.1, 1)) * 100, 100)}%`
                  }}
                ></div>
              </div>
            </div>

            <div className="pt-4 theme-border-primary border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium theme-text">Avg Reply Rate</span>
                <span className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                  {analytics.averageReplyRate}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recently Active Users - Full Width at Bottom - INDEPENDENT OF MONTH */}
      <div className="theme-card shadow-sm p-6 rounded-lg border hover:shadow-lg transition-shadow">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold theme-text flex items-center">
            <span className="mr-2">⚡</span>
            Recently Active Users
            <span className="ml-2 text-xs font-normal theme-text-muted">(Last 30 Days)</span>
          </h3>
          {isLoadingViewers && (
            <div className="flex items-center gap-2 text-sm theme-text-muted">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </div>
          )}
        </div>

        {isLoadingViewers ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse flex items-start space-x-4 p-4 theme-surface-elevated rounded-lg">
                <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : recentViewersData && recentViewersData.viewers && recentViewersData.viewers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentViewersData.viewers.map((viewer, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 theme-surface-elevated rounded-lg theme-hover transition-all hover:scale-105">
                <div className="flex-shrink-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg shadow-lg"
                    style={{
                      background: `linear-gradient(135deg, hsl(${index * 50}, 70%, 60%), hsl(${index * 50 + 30}, 70%, 50%))`
                    }}
                  >
                    {viewer.name.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold theme-text text-sm mb-1 truncate">{viewer.name}</h4>
                  <div className="text-xs theme-text-muted space-y-1">
                    <div className="truncate flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      {viewer.email}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                        {viewer.postsViewed} posts
                      </span>
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {viewer.country}
                      </span>
                    </div>
                    <div className="text-xs opacity-75">{new Date(viewer.lastActive).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 mb-4 text-gray-400 dark:text-gray-600">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="theme-text-muted font-medium">No recent activity</p>
            <p className="text-sm theme-text-muted mt-1">Users will appear here once they log in and use the platform</p>
          </div>
        )}
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  subtitle?: string;
  value: number;
  change?: string;
  percentage?: string;
  suffix?: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

function MetricCard({ title, subtitle, value, change, percentage, suffix, icon, color }: MetricCardProps) {
  const getColorStyles = (color: string) => {
    const colorMap = {
      blue: { bg: 'from-blue-500 to-blue-600', lightBg: 'bg-blue-50 dark:bg-blue-900/20' },
      green: { bg: 'from-green-500 to-green-600', lightBg: 'bg-green-50 dark:bg-green-900/20' },
      purple: { bg: 'from-purple-500 to-purple-600', lightBg: 'bg-purple-50 dark:bg-purple-900/20' },
      orange: { bg: 'from-orange-500 to-orange-600', lightBg: 'bg-orange-50 dark:bg-orange-900/20' }
    };
    return colorMap[color as keyof typeof colorMap];
  };

  const colorStyles = getColorStyles(color);
  const displayValue = (value ?? 0).toLocaleString();

  return (
    <div className="theme-card shadow-sm p-6 hover:shadow-xl transition-all hover:scale-105 rounded-lg border-2 border-transparent hover:border-blue-200 dark:hover:border-blue-800">
      <div className="flex items-center justify-between mb-4">
        <div
          className={`p-3 rounded-xl bg-gradient-to-br ${colorStyles.bg} shadow-lg`}
        >
          <span className="text-2xl">{icon}</span>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium theme-text-muted mb-1">
          {title}
          {subtitle && <span className="block text-xs opacity-75 mt-0.5">{subtitle}</span>}
        </p>
        <p className="text-3xl font-bold theme-text mb-2">
          {displayValue}{suffix}
        </p>

        {change && (
          <p className="text-xs flex items-center text-green-600 dark:text-green-400 font-medium">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            {change}
          </p>
        )}

        {percentage && (
          <p className="text-xs theme-text-muted mt-2">
            {percentage}% of total users
          </p>
        )}
      </div>
    </div>
  );
}
